/**
 * All Certifications — the repository of what people are qualified in.
 *
 * ── This is a record, not an application ──────────────────────────────────────────────────
 *
 * Adding a certification here is not a request for anything. Any employee can record a
 * certification they already hold, no nomination is involved, nobody has to be configured in
 * Settings first, and nothing goes to a manager. It is their own history.
 *
 * Reimbursement is the separate flow: Apply for Reimbursement → Certification, which does go
 * through Manager and Admin approval and which references a certification record from here.
 * Nothing on this page approves or rejects anything.
 *
 * ── What each viewer sees ─────────────────────────────────────────────────────────────────
 *
 * EVERYONE reads the whole organisation's register, and the search bar matches on employee name
 * and id for everyone with it.
 *
 * It was not always so. An ordinary employee saw only their own rows, and a MANAGER counted as
 * ordinary — the gate was canViewAllExpenses, which admits Finance, HR and Super Admin only —
 * so a manager could not see their own team. Opened deliberately: a certification is a
 * qualification somebody holds, and a register only half the company can read is not a register.
 *
 * Reading is not editing. `canAdminister` still gates the retake button to an admin or the
 * record's owner, and the server enforces the same rule independently — updateCertification is
 * a conditional write on employeeEmail for anyone who is not an admin.
 *
 * ── Only passed certifications are listed ───────────────────────────────────
 *
 * A failed attempt is still recorded — the service writes it, the retake deadline hangs off it,
 * a reimbursement claim reads it — but it is not a qualification, and this page is the list of
 * who holds what. Listing failures beside the passes told the whole company which exams a
 * colleague did not pass, which is not what a register is for.
 *
 * Hidden from the list, not from the person who has to act on it. The failed record is where a
 * retake is recorded, and for a certification with no reimbursement claim this page is the ONLY
 * place that can be done — so the attempts this viewer may update (their own, or anyone’s for an
 * admin) sit one click behind "Show failed attempts", which does not appear at all when there
 * are none. Dropping the failed cards outright would have quietly removed the only retake
 * button in the product for a self-recorded certification.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole } from '../../types/user';
import { canViewAllExpenses } from '../../utils/rolePermissions';
import { canonicalizeEmail } from '../../services/identity';
import { Certification, CertificationResult } from '../../types/certification';
import { CertificationService, describeCertificationError, filterCertifications } from '../../services/certificationService';
import {
  getCertificateUrl,
  getCertificatePreviewUrl,
  getCertificatePdfPreviewUrl,
  getCertificateThumbnailUrl,
} from '../../services/certificationDocuments';
import { isPdfDocument } from '../../services/pdfPageImage';
import AddCertificationModal from './AddCertificationModal';
import CertificateViewerModal, { ViewingCertificate } from './CertificateViewerModal';
import CertificationCard from './CertificationCard';
import './Certifications.css';

const isoToday = () => new Date().toISOString().slice(0, 10);

const formatDate = (iso?: string): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
  if (!match) return '—';
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${Number(match[3])}-${MONTHS[Number(match[2]) - 1]}-${match[1]}`;
};

/** One document's preview, and how far down the fallback chain it has got. */
type PreviewEntry = {
  /** What the card's `<img>` points at. Empty means there is nothing to draw. */
  url: string;
  /** The original document, when a browser can draw it. Empty for a PDF. */
  full: string;
  /** Whether the document is a PDF, so a dead end means "render it" rather than "give up". */
  pdf: boolean;
  /** `none` not tried, `pending` rendering now, `done` tried — whatever came of it. */
  render: 'none' | 'pending' | 'done';
};

/**
 * Is a picture of this document still on its way?
 *
 * True from the moment a PDF runs out of URLs to the moment its render finishes or fails — which
 * spans two state changes, not one, because the `<img>` error records the dead end and the effect
 * below starts the work. Reading the span rather than the second half of it is what keeps the
 * placeholder from painting for a frame in between, saying no certificate is there about a
 * certificate that is about to appear.
 */
const isPreviewOnItsWay = (entry?: PreviewEntry): boolean =>
  Boolean(entry && entry.pdf && !entry.url && entry.render !== 'done');

const AllCertificationsPage: React.FC = () => {
  const { user } = useAuth();
  const role = (user?.role as UserRole) || UserRole.EMPLOYEE;
  const myEmail = canonicalizeEmail(user?.email);

  /**
   * Everybody reads the whole organisation's record.
   *
   * This page used to show an ordinary employee only their own rows, and a MANAGER counted as
   * ordinary — canViewAllExpenses admits Finance, HR and Super Admin only — so a manager could
   * not see their own team's certifications. Opened to every role by decision: a certification
   * is a qualification somebody holds, and knowing who in the company holds what is the point
   * of keeping the register.
   */
  const seesEveryone = true;

  /**
   * Reading everyone's is not editing everyone's.
   *
   * These were ONE flag, and widening it would have handed every employee the retake button on
   * every colleague's record. The server refuses that anyway — updateCertification is a
   * conditional write on employeeEmail for non-admins — but a button that always fails is worse
   * than no button.
   */
  const canAdminister = canViewAllExpenses(role);

  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  /**
   * Whether this viewer’s own failed attempts are showing.
   *
   * Off on every load. The register is the list of qualifications; a failed attempt is one
   * person’s unfinished business, not something the page opens with.
   */
  const [showFailed, setShowFailed] = useState(false);
  const [adding, setAdding] = useState(false);
  const [retakeFor, setRetakeFor] = useState<Certification | null>(null);
  const [openingDoc, setOpeningDoc] = useState<string | null>(null);
  const [docError, setDocError] = useState('');
  /* The certificate currently on screen, or null. See CertificateViewerModal. */
  const [viewing, setViewing] = useState<ViewingCertificate | null>(null);
  /**
   * Thumbnail URLs, keyed by the S3 key rather than by certification id.
   *
   * By key because the same document can back several records — a retake keeps the original
   * attempt's document, and a reimbursement-linked record shares the file it was raised with — and
   * signing the same key twice would be two identical URLs held under two ids.
   *
   * Two URLs per key, not one. `url` is what the card draws — the small stored thumbnail when
   * there is one — and `full` is the original, held as the fallback for the records uploaded
   * before thumbnails existed. Whether a thumbnail is really in the bucket cannot be known
   * without a HeadObject the bucket CORS may refuse, so the `<img>` tries it and a failure swaps
   * in `full`. An empty `url` with no `full` left is the placeholder state.
   *
   * `pdf` and `render` are the same fallback for a PDF, which has no drawable original to swap in:
   * page one is rendered in the browser instead, once, and `render` is what keeps that from being
   * attempted twice or read as a card with nothing on it while it is still happening.
   */
  const [previews, setPreviews] = useState<Record<string, PreviewEntry>>({});

  /**
   * Whether this page is still on screen, for the PDF renders that outlive a re-render.
   *
   * A ref and not a flag in a closure — see the effect that reads it for why a closure gets that
   * particular job wrong. Unmounting is the only thing that should stop a render in flight.
   *
   * Set on the way in as well as cleared on the way out, which matters in development: StrictMode
   * mounts, unmounts and remounts every component once, and a ref that is only ever cleared would
   * spend the whole real session saying the page had gone.
   */
  const onScreen = useRef(true);
  useEffect(() => {
    onScreen.current = true;
    return () => {
      onScreen.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const rows = seesEveryone
        ? await CertificationService.listAll()
        : await CertificationService.listForEmployee(myEmail);
      setCertifications(rows);
    } catch (err: any) {
      setError(describeCertificationError(err, 'Could not load certifications.'));
      setCertifications([]);
    } finally {
      setLoading(false);
    }
  }, [seesEveryone, myEmail]);

  useEffect(() => {
    load();
  }, [load]);

  /** Whether this viewer may record a retake against this record — own, or an admin’s reach. */
  const canUpdate = useCallback(
    (c: Certification) => canAdminister || canonicalizeEmail(c.employeeEmail) === myEmail,
    [canAdminister, myEmail]
  );

  /**
   * The failed attempts this viewer could record a retake against.
   *
   * Their own, plus everyone’s for an admin — the same reach as the button on the card, so the
   * count on the toggle and the number of cards it reveals cannot disagree. Counted over every
   * loaded row rather than over `visible`, because the toggle’s whole job is to say a failed
   * attempt exists while it is still hidden.
   */
  const retakeable = useMemo(
    () => certifications.filter((c) => c.result !== CertificationResult.PASSED && canUpdate(c)),
    [certifications, canUpdate]
  );

  /**
   * What the page lists, before the search box narrows it.
   *
   * Kept apart from `visible` so the empty state can tell "nothing recorded" from "nothing
   * matches that search" — counting `certifications` for that would call a register holding one
   * failed attempt a search with no hits, and counting `visible` would call every register a
   * search with no hits the moment somebody typed.
   */
  const register = useMemo(
    () =>
      certifications.filter(
        (c) => c.result === CertificationResult.PASSED || (showFailed && canUpdate(c))
      ),
    [certifications, showFailed, canUpdate]
  );

  const visible = useMemo(
    () =>
      filterCertifications(register, search, { includePeople: seesEveryone }).sort(
        // Newest result first: the thing somebody just added is the thing they came to check.
        (a, b) => String(b.resultDate).localeCompare(String(a.resultDate))
      ),
    [register, search, seesEveryone]
  );

  /**
   * Resolve a thumbnail for every image-backed record currently on screen.
   *
   * Runs over `visible` rather than over every loaded certification, so a filtered view signs only
   * what it draws. Keys already resolved are skipped, which is what stops a keystroke in the search
   * box from re-signing the whole grid.
   *
   * Signing is local — both helpers deliberately omit the existence probe — so this issues no
   * network request per card; the only fetch is the browser loading the <img> itself. `cancelled`
   * still guards the state write, because credentials may not be resolved yet and the page can
   * unmount while that await is outstanding.
   */
  useEffect(() => {
    let cancelled = false;
    const pending = Array.from(
      new Set(
        visible
          .map((c) => c.documentKey)
          .filter((key): key is string => Boolean(key) && !(key as string in previews))
      )
    );
    if (pending.length === 0) return;

    (async () => {
      const resolved = await Promise.all(
        pending.map(
          async (key) =>
            [
              key,
              await getCertificateThumbnailUrl(key),
              await getCertificatePreviewUrl(key),
            ] as const
        )
      );
      if (cancelled) return;
      setPreviews((current) => {
        const next = { ...current };
        // A key that can produce nothing, or that failed to sign, is recorded as '' rather than
        // left out — otherwise it stays "pending" forever and this effect retries it on every
        // render.
        for (const [key, thumb, full] of resolved) {
          next[key] = {
            url: thumb || full || '',
            full: full || '',
            pdf: isPdfDocument(key),
            render: 'none',
          };
        }
        return next;
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [visible, previews]);

  /**
   * An <img> that failed to load.
   *
   * A failed THUMBNAIL is the ordinary case for anything uploaded before thumbnails existed, so
   * it falls back to the original rather than to the placeholder — a 1.7 MB certificate drawn
   * slowly still beats a card that says nothing is there. Only a failure on the original itself
   * clears the URL and lets the placeholder take over.
   *
   * Nothing is kicked off here, deliberately. This records the failure and the effect below reads
   * it: a PDF left with no URL is the state that asks for a render, so the decision lives in one
   * place whether it was reached by a failed thumbnail or by a document that never had one.
   */
  const handlePreviewFailed = useCallback((c: Certification) => {
    const key = c.documentKey;
    if (!key) return;
    setPreviews((current) => {
      const entry = current[key];
      if (!entry) return current;
      if (entry.full && entry.url !== entry.full) {
        return { ...current, [key]: { ...entry, url: entry.full } };
      }
      return { ...current, [key]: { ...entry, url: '', full: '' } };
    });
  }, []);

  /**
   * Render page one of the PDFs that have no thumbnail to show.
   *
   * Reached by a PDF whose blind `.thumb.jpg` came back 403 — every PDF uploaded before
   * thumbnails covered them. There is nothing further to point an `<img>` at, so pdf.js draws the
   * page here instead of the card printing a filename.
   *
   * Marked `pending` before the work starts rather than after, because that mark is what stops
   * this effect re-entering on a state change it caused itself.
   *
   * One at a time, on purpose: a register of PDFs would otherwise start a dozen simultaneous
   * downloads and a dozen canvas renders on the main thread's doorstep, to fill bands the reader
   * is scrolling past. `getCertificatePdfPreviewUrl` caches by key, so the cards that share a
   * document — a retake keeps the original attempt's file — wait on one render between them.
   *
   * Guarded by the ref rather than by a per-run flag, and that is not a style choice: this effect
   * writes to the state it depends on, so marking the queue `pending` re-runs it and tears down
   * the run that had just begun. A `cancelled` closure would then stop the loop it had only just
   * started, and every PDF after the first would never be drawn.
   */
  useEffect(() => {
    const waiting = Object.entries(previews)
      .filter(([, entry]) => entry.pdf && !entry.url && entry.render === 'none')
      .map(([key]) => key);
    if (waiting.length === 0) return;

    setPreviews((current) => {
      const next = { ...current };
      for (const key of waiting) {
        if (next[key]) next[key] = { ...next[key], render: 'pending' };
      }
      return next;
    });

    (async () => {
      for (const key of waiting) {
        const url = await getCertificatePdfPreviewUrl(key);
        if (!onScreen.current) return;
        setPreviews((current) =>
          current[key]
            ? { ...current, [key]: { ...current[key], url: url || '', render: 'done' } }
            : current
        );
      }
    })();
  }, [previews]);

  /*
   * The certificate opens HERE, in a dialog over the register, rather than in a new tab.
   *
   * A gallery of forty certifications produced forty tabs, one per document glanced at, and
   * coming back meant finding this page again with its search and scroll position gone. The
   * presign is unchanged — same URL, same TTL, same error — only where it is rendered.
   *
   * It also fixes a silent failure: window.open is exempt from the popup blocker only while
   * the browser can attribute it to a click, and this call came AFTER an await. On a slow
   * presign the gesture had expired and nothing happened at all.
   */
  const openDocument = async (c: Certification) => {
    setDocError('');
    setOpeningDoc(c.id);
    try {
      const url = await getCertificateUrl(c.documentKey, c.documentName || c.name);
      setViewing({ url, title: c.name, documentName: c.documentName || undefined });
    } catch (err: any) {
      setDocError(err?.message || 'The document could not be opened.');
    } finally {
      setOpeningDoc(null);
    }
  };

  const today = isoToday();

  return (
    <div className="certs">
      <div className="certs__head">
        <div>
          <h1>All Certifications</h1>
          <p>
            Every certification passed across the organisation. Adding one here is a record of a
            qualification — it is not a reimbursement request.
          </p>
        </div>
        <button type="button" className="certs__btn certs__btn--primary" onClick={() => setAdding(true)}>
          Add Certification
        </button>
      </div>

      {/* Search takes the row and the secondary controls sit together at its end, so the toolbar
          reads as one band above the gallery rather than competing boxes. Below the grid’s
          two-column breakpoint they stack, which is why the group is its own element. */}
      <div className="certs__filters">
        <input
          type="search"
          className="certs__search"
          placeholder="Search certifications..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search certifications"
        />
        <div className="certs__filters-group">
          {/* Absent rather than disabled when there is nothing to reveal: a permanent control
              offering to show failures an employee does not have reads as an accusation. */}
          {retakeable.length > 0 && (
            <button
              type="button"
              className={`certs__btn${showFailed ? ' certs__btn--on' : ''}`}
              onClick={() => setShowFailed((on) => !on)}
              aria-pressed={showFailed}
            >
              {showFailed
                ? 'Hide failed attempts'
                : `Show failed attempts (${retakeable.length})`}
            </button>
          )}
          <button type="button" className="certs__btn" onClick={load} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && <div className="certs__notice certs__notice--error" role="alert">{error}</div>}
      {docError && <div className="certs__notice certs__notice--error" role="alert">{docError}</div>}

      {/* `error &&` guards the empty state — see the note on the same guard in
          CertificationReimbursementPage. A failed read leaves `certifications` at [], which
          would otherwise print "No certifications yet." beside a notice saying the API could
          not be reached: two claims that cannot both be true, one of them invented. */}
      {loading && certifications.length === 0 ? (
        // Shaped like the grid it precedes, so the first paint does not jump from one tall block
        // to three columns of cards.
        <div className="certs__grid" aria-hidden="true">
          {[0, 1, 2, 3, 4, 5].map((n) => (
            <div key={n} className="certs__skeleton certs__skeleton--card" />
          ))}
        </div>
      ) : error ? null : visible.length === 0 ? (
        <div className="certs__empty">
          {/* Three different nothings, and they are not interchangeable. "Nothing matches that
              search" over an empty register is a lie about why the page is blank, and so is "No
              certifications yet" to somebody whose only record is a failed attempt. */}
          <h3>
            {certifications.length === 0
              ? 'No certifications yet.'
              : register.length === 0
              ? 'No passed certifications yet.'
              : 'Nothing matches that search.'}
          </h3>
          <p>
            {certifications.length === 0
              ? 'Use Add Certification to record one you have already completed.'
              : register.length === 0
              ? 'Only passed certifications are listed here.'
              : 'Try a different name or provider.'}
          </p>
        </div>
      ) : (
        <div className="certs__grid">
          {visible.map((c) => (
            <CertificationCard
              key={c.id}
              certification={c}
              today={today}
              showEmployee={seesEveryone}
              previewUrl={previews[c.documentKey || '']?.url || null}
              previewPending={isPreviewOnItsWay(previews[c.documentKey || ''])}
              opening={openingDoc === c.id}
              canUpdate={canUpdate(c)}
              formatDate={formatDate}
              onOpenDocument={openDocument}
              onRecordAttempt={setRetakeFor}
              onPreviewFailed={handlePreviewFailed}
            />
          ))}
        </div>
      )}

      {adding && (
        <AddCertificationModal
          onClose={() => setAdding(false)}
          onSaved={() => {
            setAdding(false);
            load();
          }}
        />
      )}

      {retakeFor && (
        <AddCertificationModal
          retakeOf={retakeFor}
          onClose={() => setRetakeFor(null)}
          onSaved={() => {
            setRetakeFor(null);
            load();
          }}
        />
      )}

      {viewing && <CertificateViewerModal {...viewing} onClose={() => setViewing(null)} />}
    </div>
  );
};

export default AllCertificationsPage;
